import { forwardRef } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // `disabled:saturate-50` flattens the primary variant's
  // blue→violet gradient when the button is disabled, so it stops
  // looking "ready to click" against the bright light card surface.
  // `opacity-60` alone was visually present but easy to miss because
  // the gradient itself stayed vibrant — the QA pass caught this on
  // the booking form's "Continue" button before the user had filled
  // the MRN. Combining opacity + saturation gives an unambiguous
  // disabled state on either theme.
  "inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:saturate-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-gradient-to-br from-primary to-[hsl(var(--accent-2))] text-primary-foreground shadow-md hover:brightness-105 active:translate-y-[1px]",
        secondary:
          "border border-border bg-foreground/[0.04] text-foreground hover:bg-foreground/[0.08]",
        ghost: "text-foreground hover:bg-foreground/[0.06]",
        danger:
          "border border-destructive/60 bg-destructive/10 text-destructive hover:bg-destructive/20",
        // Solid red for the final, irreversible step inside a pop-up (danger
        // is the quieter outline for in-page actions). White on rose-600/700
        // holds 4.5:1 in both themes; the dark theme's --destructive is too
        // light to carry white text.
        destructive: "bg-rose-600 text-white shadow-sm hover:bg-rose-700 active:translate-y-[1px]",
      },
      size: {
        sm: "h-8 px-3",
        md: "h-10 px-4",
        lg: "h-11 px-6 text-base",
        icon: "h-9 w-9 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
  },
);
Button.displayName = "Button";

export { buttonVariants };
